/**
 * v2 디자인 시스템 — 브랜드 × 모드 팔레트 (색/스케일/그라디언트)
 *
 * 구조 토큰(타이포·스페이싱·레이아웃 등)은 foundation.ts에서 공유하고,
 * 브랜드·모드마다 다른 색만 여기서 정의한다. 컴포넌트는 이 값을 직접 쓰지 않고
 * cssVars.ts의 `v2Color`(CSS 변수 참조)를 통해 소비한다. (카탈로그 문서만 팔레트 직접 참조)
 *
 * 값 출처: 디자이너 확정 토큰 export(Figma Variables — Color: Tingle / BubbleChat-Light / BubbleChat-Dark).
 * 시맨틱 토큰 이름은 export 기준이되 기존 소비처 호환을 위해 코드 키는 유지
 * (system: red=error, green=success, yellow=warning, blue=info, nsfw=nsfw / button.subBg=background/sub-button / brand.pinkPastel=brand/soft).
 *
 * - tingle: dark 단일 모드. bubblechat: light/dark 2모드.
 */

import { V2Scrim } from './foundation';

export type V2Mode = 'light' | 'dark';

/** Highlight 틴트 — 브랜드/시스템색의 10/30/50% 알파(Figma Color export Highlight/*). */
export type V2HighlightTints = { '10': string; '30': string; '50': string };
const tint = (r: number, g: number, b: number): V2HighlightTints => ({
  '10': `rgba(${r}, ${g}, ${b}, 0.1)`,
  '30': `rgba(${r}, ${g}, ${b}, 0.3)`,
  '50': `rgba(${r}, ${g}, ${b}, 0.5)`,
});
/** border.black — 모드 무관 검정 보더(Figma border/black). */
const borderBlack = {
  subtle: 'rgba(0, 0, 0, 0.08)',
  default: 'rgba(0, 0, 0, 0.2)',
  strong: 'rgba(0, 0, 0, 0.3)',
} as const;

/** 모든 브랜드×모드가 정의하는 시맨틱 색. (scale/gradient/heroScrim만 optional) */
export type V2Palette = {
  color: {
    brand: {
      primary: string;
      primaryPressed: string;
      secondary: string;
      pinkPastel: string;
      ink: string;
      /** brand/marketing — 마케팅 지면·로고 전용(UI 내 사용 금지). 팅글 리브랜딩에서 신설, 버블챗 미정의 */
      marketing?: string;
    };
    bg: {
      primary: string;
      secondary: string;
      tertiary: string;
      elevated: string;
      muted: string;
      dim: string;
      /** background/dim 2 — 딤보다 옅은 오버레이(카드 라벨 칩 등). 모드별로 값이 갈린다 */
      dim2: string;
    };
    label: { primary: string; secondary: string; tertiary: string; quaternary: string; onAccent: string };
    separator: string;
    border: {
      default: string;
      subtle: string;
      strong: string;
      black: { default: string; subtle: string; strong: string };
    };
    system: { red: string; green: string; yellow: string; blue: string; nsfw: string };
    static: { white: string; black: string };
    text: { accent: string };
    chat: { bubbleIn: string; bubbleOut: string };
    currency: { coin: string; potion: string };
    button: { subBg: string };
    /** 브랜드/시스템색 틴트(10/30/50%). Figma Highlight/*. */
    highlight: {
      primary: V2HighlightTints;
      secondary: V2HighlightTints;
      error: V2HighlightTints;
      success: V2HighlightTints;
      warning: V2HighlightTints;
      info: V2HighlightTints;
    };
  };
  /** 500 = base. primary/secondary는 브랜드 주색/보조색 램프. (tingle만 정의) */
  scale?: {
    primary: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
    secondary: Record<50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900, string>;
  };
  /**
   * 브랜드 그라디언트. 브랜드마다 정의하는 키가 다르다(미정의 키는 CSS 변수 자체를 선언하지 않음).
   * - brandGlow/aurora/hot/spectrum/orb: 팅글 Style Guide 그라디언트.
   * - promo: 프로모 스낵바 배경(Figma snackbar — 버블챗 3615:190104 / 팅글 3615:189836).
   *   두 브랜드 모두 `scale/primary/100 → scale/secondary/200` 좌→우.
   */
  gradient?: Partial<{ brandGlow: string; aurora: string; hot: string; spectrum: string; orb: string; promo: string }>;
  /** 히어로 이미지 위 딥스크림(`--v2-hero-scrim`). dark는 폴백(deepDark) 사용하므로 생략. */
  heroScrim?: string;
};

/** 팅글 · dark */
const tingleDark: V2Palette = {
  color: {
    brand: {
      primary: '#F95388',
      primaryPressed: '#E24278',
      secondary: '#FFF820',
      pinkPastel: '#FFDAE4',
      ink: '#0B0B0C',
      // 마케팅 지면·로고 전용. 구 brand/secondary(마젠타)가 여기로 이동했다(UI 내 사용 금지)
      marketing: '#FF3798',
    },
    bg: {
      primary: '#000000',
      secondary: '#181A18',
      tertiary: '#252825',
      elevated: '#191C19',
      muted: '#3B3F3B',
      dim: 'rgba(0, 0, 0, 0.5)',
      // 팅글 모드는 dim과 같은 값(Figma 4141:487513 실측 #00000080)
      dim2: 'rgba(0, 0, 0, 0.5)',
    },
    label: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      tertiary: 'rgba(255, 255, 255, 0.5)',
      quaternary: 'rgba(255, 255, 255, 0.3)',
      onAccent: '#0B0B0C',
    },
    separator: '#3B3F3B',
    border: {
      default: 'rgba(255, 255, 255, 0.2)',
      subtle: 'rgba(255, 255, 255, 0.08)',
      strong: 'rgba(255, 255, 255, 0.3)',
      black: borderBlack,
    },
    system: { red: '#FF453A', green: '#32D74B', yellow: '#FFD60A', blue: '#0A84FF', nsfw: '#C42EC0' },
    static: { white: '#FFFFFF', black: '#000000' },
    text: { accent: '#F95388' },
    chat: { bubbleIn: '#252825', bubbleOut: '#444441' },
    currency: { coin: '#FFCB47', potion: '#FF8A7A' },
    button: { subBg: '#444441' },
    highlight: {
      primary: tint(249, 83, 136),
      secondary: tint(255, 248, 32),
      error: tint(255, 69, 58),
      success: tint(50, 215, 75),
      warning: tint(255, 214, 10),
      info: tint(10, 132, 255),
    },
  },
  scale: {
    // pink
    primary: {
      50: '#FFF1F5',
      100: '#FEE3EC',
      200: '#FDC8D9',
      300: '#FCA9C4',
      400: '#FB80A7',
      500: '#F95388',
      600: '#D14672',
      700: '#A9385C',
      800: '#812B47',
      900: '#5A1E31',
    },
    // lime
    secondary: {
      50: '#FFFEED',
      100: '#FFFEDB',
      200: '#FFFDB8',
      300: '#FFFB8B',
      400: '#FFFA56',
      500: '#FFF820',
      600: '#D6D01B',
      700: '#ADA916',
      800: '#858111',
      900: '#5C590C',
    },
  },
  gradient: {
    brandGlow: 'linear-gradient(159.15deg, #F95388 14.64%, #FFF820 85.36%)',
    aurora: 'linear-gradient(159.15deg, #F95388 14.64%, #FFDAE4 85.36%)',
    hot: 'linear-gradient(159.15deg, #FFF820 14.64%, #FFDAE4 85.36%)',
    spectrum: 'linear-gradient(159.15deg, #F95388 14.64%, #FFF820 50%, #FFDAE4 85.36%)',
    orb: 'linear-gradient(159.15deg, #FFF820 14.64%, #A05CFF 50%, #5CC8FF 85.36%)',
    // scale.primary[100] → scale.secondary[200]
    promo: 'linear-gradient(90deg, #FEE3EC 0%, #FFFDB8 100%)',
  },
};

/**
 * 버블챗 scale 램프. light/dark 값이 동일해 한 곳에 둔다.
 * (Color export 기준 — BubbleChat-Dark/Light 두 팔레트 값 일치 확인)
 */
const bubblechatScale = {
  // cyan
  primary: {
    50: '#E9FCFF',
    100: '#B0EAF4',
    200: '#7CDCEC',
    300: '#43CEE4',
    400: '#43CEE4',
    500: '#00E0FF',
    600: '#00C3DD',
    700: '#0094A9',
    800: '#008091',
    900: '#005E65',
  },
  // violet
  secondary: {
    50: '#F6F3FC',
    100: '#ECE6F7',
    200: '#CFC2EA',
    300: '#B099DD',
    400: '#906FD0',
    500: '#AE85FD',
    600: '#906FD0',
    700: '#4625AD',
    800: '#381EA6',
    900: '#281F91',
  },
} as const;

/** scale.primary[100] → scale.secondary[200] */
const bubblechatPromoGradient = `linear-gradient(90deg, ${bubblechatScale.primary[100]} 0%, ${bubblechatScale.secondary[200]} 100%)`;

/** 버블챗 · light */
const bubblechatLight: V2Palette = {
  color: {
    brand: {
      primary: '#00E0FF',
      primaryPressed: '#00C3DD',
      secondary: '#AE85FD',
      pinkPastel: '#FFE3E6',
      ink: '#131415',
    },
    bg: {
      primary: '#F8F8F8',
      secondary: '#FFFFFF',
      tertiary: '#F1F1F4',
      elevated: '#FFFFFF',
      muted: '#D4D4D4',
      dim: 'rgba(0, 0, 0, 0.5)',
      // 버블챗 모드는 훨씬 옅다(Figma 4141:554801 실측 #0000001A)
      dim2: 'rgba(0, 0, 0, 0.1)',
    },
    label: {
      primary: '#000000',
      secondary: 'rgba(0, 0, 0, 0.7)',
      tertiary: 'rgba(0, 0, 0, 0.5)',
      quaternary: 'rgba(0, 0, 0, 0.3)',
      onAccent: '#131415',
    },
    separator: '#E7E7E7',
    border: {
      default: 'rgba(0, 0, 0, 0.12)',
      subtle: 'rgba(0, 0, 0, 0.08)',
      strong: 'rgba(0, 0, 0, 0.2)',
      black: borderBlack,
    },
    system: { red: '#F6375A', green: '#00CF94', yellow: '#FFCB47', blue: '#229BDC', nsfw: '#A3229B' },
    static: { white: '#FFFFFF', black: '#000000' },
    text: { accent: '#008091' },
    chat: { bubbleIn: '#F1F1F4', bubbleOut: '#DBE0EA' },
    currency: { coin: '#FFCB47', potion: '#FF8A7A' },
    button: { subBg: '#E7E7E7' },
    highlight: {
      primary: tint(0, 224, 255),
      secondary: tint(174, 133, 253),
      error: tint(246, 55, 90),
      success: tint(0, 207, 148),
      warning: tint(255, 203, 71),
      info: tint(34, 155, 220),
    },
  },
  scale: bubblechatScale,
  gradient: { promo: bubblechatPromoGradient },
  // light 히어로 딥스크림 — 하단을 밝은 배경(#F8F8F8)으로 페이드
  heroScrim: V2Scrim.deepLight,
};

/** 버블챗 · dark */
const bubblechatDark: V2Palette = {
  color: {
    brand: {
      primary: '#00E0FF',
      primaryPressed: '#00C3DD',
      secondary: '#AE85FD',
      pinkPastel: '#FFE3E6',
      ink: '#131415',
    },
    bg: {
      primary: '#131415',
      secondary: '#242526',
      tertiary: '#323434',
      elevated: '#242526',
      muted: '#2C2C33',
      dim: 'rgba(0, 0, 0, 0.5)',
      // Tingle DS에 버블챗 다크 모드가 없어 팅글 다크와 동일하게 둔다
      // (다크 배경에 10% 검정은 거의 보이지 않는다). DS에 값이 생기면 교체할 것
      dim2: 'rgba(0, 0, 0, 0.5)',
    },
    label: {
      primary: '#FFFFFF',
      secondary: 'rgba(255, 255, 255, 0.7)',
      tertiary: 'rgba(255, 255, 255, 0.5)',
      quaternary: 'rgba(255, 255, 255, 0.3)',
      onAccent: '#131415',
    },
    separator: '#41424D',
    border: {
      default: 'rgba(255, 255, 255, 0.2)',
      subtle: 'rgba(255, 255, 255, 0.08)',
      strong: 'rgba(255, 255, 255, 0.3)',
      black: borderBlack,
    },
    system: { red: '#FF5C5C', green: '#00CF94', yellow: '#FFCB47', blue: '#229BDC', nsfw: '#D24ECF' },
    static: { white: '#FFFFFF', black: '#000000' },
    text: { accent: '#00E0FF' },
    chat: { bubbleIn: '#323434', bubbleOut: '#41424D' },
    currency: { coin: '#FFCB47', potion: '#FF8A7A' },
    button: { subBg: '#41424D' },
    highlight: {
      primary: tint(0, 224, 255),
      secondary: tint(174, 133, 253),
      error: tint(255, 92, 92),
      success: tint(0, 207, 148),
      warning: tint(255, 203, 71),
      info: tint(34, 155, 220),
    },
  },
  scale: bubblechatScale,
  // 프로모 스낵바는 파스텔 배경 위 고정 흑자라 dark도 light와 동일 그라디언트
  gradient: { promo: bubblechatPromoGradient },
};

/**
 * 제휴(리워드) 웹 팔레트 — 브랜드색은 서비스 브랜드, 중립 표면은 라이트 고정.
 *
 * 리워드 웹은 다크모드가 없는 라이트 단일 표면이라(`GlobalProvider`의 `forceLightMode`)
 * dark 단일 모드인 팅글 팔레트를 그대로 주입하면 배경/라벨이 전부 뒤집힌다.
 * 그래서 중립(bg/label/border/separator/system)은 라이트 값을 쓰고, **브랜드 파생 토큰만**
 * 브랜드 팔레트에서 가져온다. 리워드 시안(Figma 1072:58012 — 밝은 회색 페이지 + 흰 카드 +
 * 핑크 CTA)의 구성과 일치한다.
 *
 * v2Brands에 등록하지 않는 이유: `resolvePalette('tingle', 'light')`가 이 팔레트로 잡히면
 * 챗(다크 강제)·카탈로그의 기존 해석이 바뀐다. 리워드 전용 파생으로 분리해 blast radius를 막는다.
 */
export function affiliatePalette(brand: V2Brand): V2Palette {
  if (brand !== 'tingle') return bubblechatLight;
  return {
    ...bubblechatLight,
    color: {
      ...bubblechatLight.color,
      brand: tingleDark.color.brand,
      // 핑크(#F95388)는 흰 배경에서 대비가 안 나와 accent 텍스트만 램프 700(scale/primary/700)을 쓴다.
      text: { accent: '#A9385C' },
      highlight: {
        ...bubblechatLight.color.highlight,
        primary: tingleDark.color.highlight.primary,
        secondary: tingleDark.color.highlight.secondary,
      },
    },
    scale: tingleDark.scale,
    gradient: tingleDark.gradient,
  };
}

/** 등록된 브랜드×모드 팔레트. tingle=dark만. */
export const v2Brands = {
  tingle: { dark: tingleDark },
  bubblechat: { light: bubblechatLight, dark: bubblechatDark },
} satisfies Record<string, Partial<Record<V2Mode, V2Palette>>>;

export type V2Brand = keyof typeof v2Brands;

/** 브랜드의 가용 모드 목록(정의된 순서). */
export const brandModes = (brand: V2Brand): V2Mode[] => Object.keys(v2Brands[brand]) as V2Mode[];

/** 브랜드+모드 → 팔레트. 요청 모드가 없으면 첫 가용 모드로 폴백. */
export function resolvePalette(brand: V2Brand, mode?: V2Mode): V2Palette {
  const modes = v2Brands[brand] as Partial<Record<V2Mode, V2Palette>>;
  const resolved = (mode && modes[mode]) || modes[brandModes(brand)[0]];
  return resolved as V2Palette;
}
